import { enqueueSnackbar } from '../components/snackbar';
import { reportingService } from '../api/reporting.service';

const STORAGE_KEY = 'opto_pending_report_tasks';
const POLL_MS = 3000;

type PendingReportTask = {
  id: number;
  name: string;
  format: string;
};

const activePollers = new Map<number, ReturnType<typeof setInterval>>();

function loadPendingTasks(): PendingReportTask[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function savePendingTasks(tasks: PendingReportTask[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
}

function removePendingTask(taskId: number) {
  savePendingTasks(loadPendingTasks().filter((task) => task.id !== taskId));
}

function extensionForFormat(format: string) {
  if (format === 'excel') return 'xlsx';
  if (format === 'pdf') return 'pdf';
  return 'csv';
}

function stopPolling(taskId: number) {
  const interval = activePollers.get(taskId);
  if (interval) {
    clearInterval(interval);
    activePollers.delete(taskId);
  }
}

function startPolling(task: PendingReportTask) {
  if (activePollers.has(task.id)) return;

  const poll = async () => {
    try {
      const status = await reportingService.getTaskStatus(task.id);
      const format =
        status?.filters?.__reportFormat || task.format || 'csv';

      if (status.status === 'completed') {
        stopPolling(task.id);
        removePendingTask(task.id);
        enqueueSnackbar(`${task.name} is ready. Your download will start shortly.`, {
          variant: 'success',
        });
        await reportingService.downloadTaskFile(
          task.id,
          `${task.name.replace(/\s+/g, '_')}.${extensionForFormat(format)}`,
        );
      } else if (status.status === 'failed') {
        stopPolling(task.id);
        removePendingTask(task.id);
        enqueueSnackbar(
          `Failed to generate ${task.name}: ${status.error || 'Unknown error'}`,
          { variant: 'error' },
        );
      }
    } catch (error) {
      console.error('Report task polling error:', error);
    }
  };

  const interval = setInterval(poll, POLL_MS);
  activePollers.set(task.id, interval);
  poll();
}

export function trackReportTask(
  taskId: number,
  reportName: string,
  format: string = 'csv',
) {
  const tasks = loadPendingTasks();
  if (!tasks.some((task) => task.id === taskId)) {
    tasks.push({ id: taskId, name: reportName, format });
    savePendingTasks(tasks);
  }
  startPolling({ id: taskId, name: reportName, format });
}

export function resumePendingReportTasks() {
  loadPendingTasks().forEach((task) => startPolling(task));
}
